/**
 * Converts a relative image path from the backend into a fully qualified URL.
 *
 * Handles:
 *  - null / undefined / empty → returns fallback (default empty string)
 *  - Already absolute (http / https / data:) → returned as-is
 *  - Legacy backend /storage URLs → routed through /api/media
 *  - Relative paths starting with "/" → prepended with VITE_BACKEND_URL
 *  - Relative paths without leading "/" → prepended with VITE_BACKEND_URL + "/"
 */

const RAW_BACKEND_URL = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/+$/, '');
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/api$/, '');
const BACKEND_ORIGIN = getOrigin(BACKEND_URL);
const MEDIA_FOLDERS = new Set([
  'avatars',
  'covers',
  'favicons',
  'logos',
  'payment_proofs',
  'payment_qr',
  'platform',
  'products',
  'shipping',
  'stores',
  'uploads',
]);

function getOrigin(url: string): string {
  if (!url) return '';

  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

function getMediaPath(pathname: string): string | null {
  const storagePrefix = pathname.startsWith('/storage/app/public/')
    ? '/storage/app/public/'
    : pathname.startsWith('/public/storage/')
    ? '/public/storage/'
    : '/storage/';

  if (pathname.startsWith('/api/media/')) return pathname;
  if (!pathname.startsWith(storagePrefix)) {
    const relativePath = pathname.replace(/^\/+/, '');
    const [folder] = relativePath.split('/');

    if (folder && MEDIA_FOLDERS.has(folder)) {
      return `/api/media/${relativePath}`;
    }

    return null;
  }

  return `/api/media/${pathname.slice(storagePrefix.length)}`;
}

export function getImageUrl(path: string | null | undefined, fallback = ''): string {
  if (!path) return fallback;
  const cleanInput = path.trim();
  if (!cleanInput) return fallback;

  if (cleanInput.startsWith('data:') || cleanInput.startsWith('blob:')) {
    return cleanInput;
  }

  if (cleanInput.startsWith('http://') || cleanInput.startsWith('https://')) {
    try {
      const url = new URL(cleanInput);
      const mediaPath = getMediaPath(url.pathname);
      const isLocalBackend = url.hostname === 'localhost' || url.hostname === '127.0.0.1';

      if (mediaPath && (isLocalBackend || url.origin === BACKEND_ORIGIN)) {
        return `${BACKEND_URL}${mediaPath}${url.search}`;
      }
    } catch {
      return cleanInput;
    }

    return cleanInput;
  }

  // Ensure a single leading slash
  const relativePath = cleanInput.startsWith('/') ? cleanInput : `/${cleanInput}`;
  const cleanPath = getMediaPath(relativePath) || relativePath;
  const baseUrl = BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${baseUrl}${cleanPath}`;
}

const RESPONSIVE_WIDTHS = [320, 640, 960, 1280];

function getResponsiveVariantUrl(path: string, width: number): string | null {
  const url = getImageUrl(path);
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return null;

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : BACKEND_URL || undefined);
    const pathname = parsed.pathname;
    const match = pathname.match(/^(.*?)(?:-\d+w)?\.(webp|avif)$/i);

    if (!match) return null;

    parsed.pathname = `${match[1]}-${width}w.${match[2]}`;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function getImageSrcSet(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  const sourceUrl = getImageUrl(path);
  const maxWidth = sourceUrl.match(/-(\d+)w\.(webp|avif)$/i)?.[1];
  const widths = maxWidth
    ? [...RESPONSIVE_WIDTHS.filter((width) => width < Number(maxWidth)), Number(maxWidth)]
    : RESPONSIVE_WIDTHS;

  const entries = widths
    .filter((width, index, list) => list.indexOf(width) === index)
    .map((width) => {
      const url = getResponsiveVariantUrl(path, width);
      return url ? `${url} ${width}w` : null;
    })
    .filter(Boolean);

  return entries.length ? entries.join(', ') : undefined;
}

export function getImageLoadingProps(
  path: string | null | undefined,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
) {
  return {
    src: getImageUrl(path),
    srcSet: getImageSrcSet(path),
    sizes,
    loading: 'lazy' as const,
    decoding: 'async' as const,
  };
}

export function parseImageList(value: unknown): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => parseImageList(item))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    return parseImageList(JSON.parse(trimmed));
  } catch {
    return [trimmed];
  }
}

export function getPrimaryProductImage(product: any): string | null {
  const candidates = [
    product?.image,
    product?.image_url,
    product?.featured_image,
    ...parseImageList(product?.images),
  ];

  return candidates.find((image) => typeof image === 'string' && image.trim())?.trim() ?? null;
}
